import React from "react";
import { Controller } from "react-hook-form";
import { Col, Label, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Stack } from "react-bootstrap";
import { useLocalizedText } from "../../utils/hooks/useLocalizedText";
import { AntSelect } from "../../CommonElements/AntSelect";
import { AntInput } from "../../CommonElements/AntInput";
import { GroupsModal } from "./CreateUser/GroupsModal";
import { PlatformPermissions } from "./CreateUser/PlatformPermissions";
import { UserFormSkeleton } from "./UserFormSkeleton";
import UploadProfileImage from "./CreateUser/UploadProfileImage";
import {
  ArabicNameField,
  EnglishNameField,
  NationalIdField,
  NationalityField,
  GenderField,
  MobileField,
} from "../ProfileForm";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";

export const UserForm = ({
  control,
  errors,
  watch,
  setValue,
  basicData,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();

  // Show skeleton when loading
  if (loading) {
    return <UserFormSkeleton />;
  }

  return (
    <Row>
      {/* Basic Profile Fields */}
      <ArabicNameField control={control} errors={errors} />
      <EnglishNameField control={control} errors={errors} />
      <NationalIdField control={control} errors={errors} />
      <NationalityField
        control={control}
        errors={errors}
        basicData={basicData}
      />
      <GenderField control={control} errors={errors} />
      <MobileField control={control} errors={errors} watch={watch} />

      {/* Existing fields */}
      <Col md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="email">
            {t("EMAIL")}
          </Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("FIELD_REQUIRED"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("EMAIL_INVALID"),
              },
            }}
            render={({ field }) => (
              <AntInput
                dir="ltr"
                id="email"
                name="email"
                type="text"
                {...field}
                status={errors.email ? "error" : undefined}
              />
            )}
          />
          {errors.email && (
            <div className="text-danger mt-1">{errors.email.message}</div>
          )}
        </Stack>
      </Col>

      <Col md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="job_id">
            {t("JOB")}
          </Label>
          <Controller
            name="job_id"
            control={control}
            rules={{ required: t("FIELD_REQUIRED") }}
            render={({ field }) => (
              <SelectCachedOptions
                id="job_id"
                showSearch
                disabled={loading}
                placeholder={t("CHOOSE_JOB")}
                status={errors.job_id ? "error" : undefined}
                optionKey="job_opportunities"
                options={(job) => ({
                  value: job.id,
                  label: (
                    <LabelValue.Value
                      value={getLocalizedText(job.name_ar, job.name_en)}
                    />
                  ),
                })}
                {...field}
              />
            )}
          />
          {errors.job_id && (
            <div className="text-danger mt-1">{errors.job_id.message}</div>
          )}
        </Stack>
      </Col>

      <Col xs={6} md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="type">
            {t("TYPE")}
          </Label>
          <Controller
            name="type"
            control={control}
            rules={{ required: t("FIELD_REQUIRED") }}
            render={({ field }) => (
              <SelectCachedOptions
                id="type"
                showSearch
                disabled={loading}
                placeholder={t("CHOOSE_TYPE")}
                status={errors.type ? "error" : undefined}
                optionKey="employee_types"
                options={(job) => ({
                  value: job.name,
                  label: (
                    <LabelValue.Value
                      value={getLocalizedText(
                        job.display_name_ar,
                        job.display_name_en
                      )}
                    />
                  ),
                })}
                {...field}
              />
            )}
          />
          {errors.type && (
            <div className="text-danger mt-1">{errors.type.message}</div>
          )}
        </Stack>
      </Col>

      <Col xs={6} md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="job_destination_id">
            {t("EMPLOYER")}
          </Label>
          <Controller
            name="job_destination_id"
            control={control}
            rules={{ required: t("FIELD_REQUIRED") }}
            render={({ field }) => (
              <SelectCachedOptions
                id="job_destination_id"
                showSearch
                disabled={loading}
                placeholder={t("CHOOSE_EMPLOYER")}
                status={errors.job_destination_id ? "error" : undefined}
                optionKey="job_destinations"
                options={(dist) => ({
                  value: dist.id,
                  label: (
                    <LabelValue.Value
                      value={getLocalizedText(dist.name_ar, dist.name_en)}
                    />
                  ),
                })}
                {...field}
              />
            )}
          />
          {errors.job_destination_id && (
            <div className="text-danger mt-1">
              {errors.job_destination_id.message}
            </div>
          )}
        </Stack>
      </Col>

      <Col xs={6} md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="sector_id">
            {t("SECTOR")}
          </Label>
          <Controller
            name="sector_id"
            control={control}
            rules={{ required: t("FIELD_REQUIRED") }}
            render={({ field }) => (
              <SelectCachedOptions
                id="sector_id"
                showSearch
                disabled={loading}
                placeholder={t("CHOOSE_SECTOR")}
                status={errors.sector_id ? "error" : undefined}
                optionKey="job_sectors"
                options={(sector) => ({
                  value: sector.id,
                  label: (
                    <LabelValue.Value
                      value={getLocalizedText(sector.name_ar, sector.name_en)}
                    />
                  ),
                })}
                {...field}
              />
            )}
          />
          {errors.sector_id && (
            <div className="text-danger mt-1">{errors.sector_id.message}</div>
          )}
        </Stack>
      </Col>

      <Col xs={6} md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold required" for="department_id">
            {t("DEPARTMENT")}
          </Label>
          <Controller
            name="department_id"
            control={control}
            rules={{ required: t("FIELD_REQUIRED") }}
            render={({ field }) => (
              <SelectCachedOptions
                id="department_id"
                showSearch
                {...field}
                disabled={loading}
                placeholder={t("CHOOSE_DEPARTMENT")}
                status={errors.department_id ? "error" : undefined}
                optionKey="departments"
                options={(department) => ({
                  value: department.id,
                  label: (
                    <LabelValue.Value
                      value={getLocalizedText(
                        department.name_ar,
                        department.name_en
                      )}
                    />
                  ),
                })}
              />
            )}
          />
          {errors.department_id && (
            <div className="text-danger mt-1">
              {errors.department_id.message}
            </div>
          )}
        </Stack>
      </Col>
      <Col xs={6} md={4}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for="service_center_id">
            {t("SERVICE_CENTER")}
          </Label>
          <Controller
            name="service_center_id"
            control={control}
            render={({ field }) => (
              <SelectCachedOptions
                id="service_center_id"
                showSearch
                allowClear
                {...field}
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value ?? null)}
                disabled={loading}
                placeholder={t("SERVICE_CENTER")}
                status={errors.service_center_id ? "error" : undefined}
                optionKey="service_centers"
                options={(service_center) => ({
                  value: service_center.id,
                  label: [service_center.name, t(service_center.system.toUpperCase())].filter(Boolean).join(" - "),
                })}
              />
            )}
          />
          {errors.service_center_id && (
            <div className="text-danger mt-1">
              {errors.service_center_id.message}
            </div>
          )}
        </Stack>
      </Col>

      <Col xs={12}>
        <UploadProfileImage control={control} />
      </Col>
      <Col md={12}>
        <PlatformPermissions
          permissions={basicData?.systems || []}
          control={control}
          setValue={setValue}
          watch={watch}
          errors={errors}
        />
      </Col>

      <Col md={4}>
        <GroupsModal
          groups={basicData?.groups || []}
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
        />
      </Col>
    </Row>
  );
};
